import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

import { Skeleton } from '@/components/ui/skeleton'

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-950 disabled:pointer-events-none disabled:opacity-50 antialiased hover:cursor-pointer gap-2 text-gray-950 dark:text-white',
  {
    variants: {
      variant: {
        default:
          'bg-gray-950 dark:bg-gray-200 text-gray-50 dark:text-gray-950 hover:bg-gray-900/90',
        brand: 'bg-blue-600 hover:bg-blue-700 text-white',
        destructive: 'bg-red-50 text-red-500 hover:bg-red-100',
        'outline-destructive':
          'bg-red-50 text-red-500 border border-red-200/60 hover:bg-red-100',
        outline:
          'border dark:border-gray-900/10 border-gray-100 bg-white dark:bg-gray-900/20 hover:shadow-none shadow-card hover:bg-gray-100 hover:text-gray-900',
        secondary: 'bg-gray-100/60 text-gray-900 hover:bg-gray-200/60',
        ghost: 'bg-transparent hover:bg-gray-100 hover:text-gray-900',
        link: 'text-gray-900 underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-11 px-4 py-2',
        sm: 'h-9 rounded-[8px] px-3 text-sm',
        lg: 'h-[52px] rounded-lg px-4',
        icon: 'size-11',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, type = 'button', asChild = false, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        type={type}
        {...props}
      />
    )
  },
)
Button.displayName = 'Button'

interface ButtonSkeletonProps
  extends React.HtmlHTMLAttributes<HTMLDivElement>,
    VariantProps<typeof buttonVariants> {}

const ButtonSkeleton = ({
  className,
  size,
  variant = 'secondary',
  ...props
}: ButtonSkeletonProps) => {
  return (
    <Skeleton
      className={cn(buttonVariants({ size, variant, className }))}
      {...props}
    >
      <Skeleton className="h-2 w-[80px] rounded-full bg-white" />
    </Skeleton>
  )
}

ButtonSkeleton.displayName = 'ButtonSkeleton'

export { Button, ButtonSkeleton, buttonVariants }
